<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import { defineAsyncMicroApp, unmountApp } from "@gohighlevel/ghl-module-federation-plugin";
import { UISpin } from "@gohighlevel/ghl-ui";
import { markRaw, onBeforeUnmount, onMounted, ref } from "vue";

const appStore = useAppStore();
const emitter = useEmitter();

// Minimal Vuex-shaped shim. The funnel-website-analytics MFA reads exactly:
//   - store.state.locations.currentLocation.id        (locationId; API calls)
//   - store.getters['LevelUpDayFlag/isfeatursActive'] (boolean value)
//   - store.getters['featureFlags/getForLocation']    (fn: key -> boolean)
// `id` is a getter so reads stay live if appStore.locationId is reseeded
// (e.g. parent Postmate handshake updates after a tenant switch).
const hostStore = {
  state: {
    locations: {
      currentLocation: {
        get id() {
          return appStore.locationId;
        },
      },
    },
  },
  getters: {
    "LevelUpDayFlag/isfeatursActive": false,
    "featureFlags/getForLocation": () => true,
  },
};

const analyticsComponent = ref<any>(null);
const analyticsProps = ref<any>(null);
const isLoading = ref<boolean>(true);

const mountAnalytics = async (): Promise<void> => {
  try {
    // The analytics MFA's axios layer calls window.getToken() to attach the
    // bearer token. The builder's host doesn't set it, so provide a fallback
    // backed by the Postmate-seeded appStore.tokenId.
    if (typeof (window as any).getToken !== "function") {
      (window as any).getToken = async (): Promise<string> => {
        return appStore.tokenId || "";
      };
    }

    const [Analytics] = await defineAsyncMicroApp("funnelWebsiteAnalytics", ["Analytics"]);

    const product = appStore.product === "survey" ? "survey" : "form";
    const entityId =
      product === "survey" ? appStore.surveyId || "" : appStore.formId || "";

    // The funnel-website-analytics MFA's setupVueI18n keys translations off
    // the underscore form (`en_US`). localStorage stores the hyphen form
    // (`en-US`), so normalize before passing — otherwise the MFA falls back
    // to English silently. spm-ts's FunnelWebsiteAnalytics.vue uses the same
    // underscore form (derived from store.state.user.user.platformLanguage).
    const locale = (localStorage.getItem("locale") || "en-US").replace("-", "_");

    analyticsProps.value = {
      emitter,
      store: hostStore,
      locale,
      embedConfig: {
        type: product,
        entityId,
        syncWithUrl: false,
        hideHeader: true,
      },
    };
    analyticsComponent.value = markRaw(Analytics);
  } catch (error) {
    console.error("Failed to mount analytics:", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(mountAnalytics);

onBeforeUnmount(() => {
  unmountApp("funnelWebsiteAnalytics");
});
</script>

<template>
  <!-- The builder shell is 100vh overflow:hidden with SecondaryHeader (~50px)
       in flow above the content area. Anything using `h-full` inherits the
       container's full 100vh and its bottom ~50px is clipped below the viewport
       — last rows / pagination unreachable. Bound the wrapper to the actual
       visible area so its scrollbar reaches the real bottom of the content. -->
  <div
    id="fsb-analytics-page"
    class="relative w-full overflow-y-auto bg-[#f2f4f7] h-[calc(100vh-90px)]"
  >
    <div
      v-if="isLoading"
      class="flex min-h-screen -translate-y-32 items-center justify-center"
    >
      <UISpin size="large" />
    </div>
    <component
      :is="analyticsComponent"
      v-bind="analyticsProps"
      v-if="analyticsComponent"
    />
  </div>
</template>
